import React from 'react';
import contensis from 'Common/api/contensis';
import { Entry, Op, OrderBy } from 'contensis-delivery-api';
import cmsParser from 'Common/util/cmsParser';
import slugify from 'slugify';
import qs from 'qs';
import setCacheHeaders from 'Common/func/setCacheHeaders';
import { useRouter } from 'next/router';
import type { NextPageWithLayout, GetServerSideProps } from 'next';

import { Filter, FilterGroup } from 'Common/hooks/useFilters';
import type { ToggleProps } from 'Elements/Input/Toggle';

import MainLayout from 'Layout/Main';
import Metadata from 'Components/Metadata';

import Template, { datamap, PageData, QueryFilter } from 'Templates/Course/Listing';

const CourseListing: NextPageWithLayout<PageData> = ({
    entry,
    ancestors,
    filters,
    toggle,
    activeFilterQueries,
    activeAcademicYear,
    courses,
    courseVariants,
    searchTerm
}) => {
    const router = useRouter();
    const metadata = cmsParser.metadata(entry);

    return (
        <>
            <Metadata
                {...metadata}
                canonical={
                    router.pathname +
                    (router.query.page && router.query.page !== '1'
                        ? `?page=${router.query.page}`
                        : '')
                }
                title={
                    metadata.title +
                    (router.query.page && router.query.page !== '1'
                        ? ` - page ${router.query.page}`
                        : '')
                }
            />
            <Template
                {...datamap({
                    entry,
                    ancestors,
                    filters,
                    toggle,
                    activeFilterQueries,
                    activeAcademicYear,
                    courses,
                    courseVariants,
                    searchTerm
                })}
            />
        </>
    );
};

CourseListing.getLayout = (page: React.ReactElement<any>, props): React.ReactElement<any> => {
    return <MainLayout {...props}>{page}</MainLayout>;
};

// TODO Replace any with PageData when Entry model has been updated
export const getServerSideProps: GetServerSideProps<any> = async ({ query, preview, res }) => {
    const { fetch, surrogateKeys } = contensis({ preview, fetchSurrogateKeys: true });

    const { data, error } = await fetch(async (client, search) => {
        const resPage = await client.nodes.get({
            path: `/courses`,
            entryLinkDepth: 4,
            entryFields: ['*']
        });

        if (!resPage.entry) {
            return {
                error: true
            };
        }

        const entry = resPage.entry;
        const ancestors = await client.nodes.getAncestors({
            id: resPage.id,
            entryFields: ['title']
        });

        /* Get course categories */

        const courseCategoriesQuery = Op.equalTo(
            'sys.contentTypeId',
            resPage.entry.courseCategoryContentiD
        );
        const resCategories = await search(courseCategoriesQuery, {
            pageSize: 25,
            orderBy: OrderBy.asc('name')
        });

        // Create filter function from label and value
        const createFilter = (label: string, value: string): Filter => ({
            id: slugify(value, { lower: true }),
            name: slugify(label, { lower: true }),
            label: label,
            value: value
        });

        // Create courses filters
        const filters: FilterGroup[] = [
            {
                type: 'studyLevels',
                label: 'Study level',
                options: entry.studyLevelFilter.map(level => createFilter(level.label, level.value))
            },
            {
                type: 'courseOptions',
                label: 'Course options',
                options: entry.courseOptionsFilter.map(level =>
                    createFilter(level.label, level.value)
                )
            },
            {
                type: 'courseCategories',
                label: 'Course category',
                options: resCategories.items.map(category =>
                    createFilter(category.name, category.sys.id)
                )
            }
        ];

        const toggle: ToggleProps = {
            id: 'academic-years',
            options: [
                {
                    label: entry.academicYearFilters[0].name,
                    value: entry.academicYearFilters[0].name
                },
                {
                    label: entry.academicYearFilters[1].name,
                    value: entry.academicYearFilters[1].name
                }
            ]
        };

        // Function to parse filter query
        const parseFilterQuery = (query: string | string[]): QueryFilter[] => {
            // If string, split commas
            if (typeof query === 'string') {
                query = query.split(',');
            }
            // If an array, join into a single array
            if (Array.isArray(query)) {
                query = query.map(item => item.split(',')).flat();
            }

            return query.map(value => {
                if (value.includes('+')) {
                    // Get the primary value, and the subFilters by spliting +
                    const [primaryValue, subFilters] = value.split('+');
                    // Parse subfilters with qs.parse
                    return {
                        id: primaryValue,
                        subFilters: Object.values(qs.parse(subFilters))
                    } as QueryFilter;
                }
                return { id: value };
            });
        };

        /* Get course results */

        // Create study levels sub-query
        const queryStudyLevels = parseFilterQuery(query.studyLevels || []);

        const studyLevelsQuery = [
            Op.or(
                ...queryStudyLevels.map(item =>
                    Op.equalTo('courseListingFilterOptions.studyLevel', item.id)
                )
            )
        ];

        // Create course options sub-query
        const queryCourseOptions = parseFilterQuery(query.courseOptions || []);

        const courseOptionsFilter = filters.find(
            item => item.type === 'courseOptions'
        ) as FilterGroup;
        const courseOptionsQuery = [
            Op.or(
                ...queryCourseOptions.map(item => {
                    const option = courseOptionsFilter.options.find(
                        option => option.id === item.id
                    ) as Filter;
                    return Op.equalTo('courseListingFilterOptions.courseOptions', option.label);
                })
            )
        ];

        // Create categories sub-query
        const queryCategories = parseFilterQuery(query.courseCategories || []);

        const categoriesQuery = [
            Op.or(...queryCategories.map(item => Op.equalTo('courseCategory.sys.id', item.id)))
        ];

        // Create academic year sub-query
        const queryYearName: string =
            (query.year && (typeof query.year === 'string' ? query.year : query.year[0])) ||
            entry.defaultYear.name;
        const queryYearKey: string = entry.academicYearFilters.find(
            year => year.name === queryYearName
        ).key;

        const academicYearQuery = [
            Op.in('courseListingFilterOptions.academicYears', ...[...new Set([queryYearKey])])
        ];

        // Get search term query
        const searchTerm = Array.isArray(query.search)
            ? query.search.join(' ')
            : query.search
            ? query.search
            : null;

        const searchTermQueries = searchTerm
            ? [
                  Op.contains('name', searchTerm).weight(100),
                  Op.freeText('courseDescription', searchTerm).weight(50),
                  Op.freeText('pageMetadata.keywords', searchTerm).weight(25),
                  Op.freeText('pageMetadata.description', searchTerm).weight(25),
                  Op.contains('sys.contentTypeId', searchTerm).weight(10)
              ]
            : [];

        const coursesQuery = [
            Op.exists('sys.uri', true),
            Op.in(
                'sys.contentTypeId',
                'courseUndergraduate',
                'courseFoundationYears',
                'coursePostgraduate',
                'courseApprenticeships',
                'courseProfessional'
            ),
            Op.and(...studyLevelsQuery),
            Op.and(...courseOptionsQuery),
            Op.and(...categoriesQuery),
            Op.and(...academicYearQuery),
            Op.or(...searchTermQueries)
        ];

        const resCourses = await search(coursesQuery, {
            pageSize: 8,
            pageIndex: parseInt(query.page as string) - 1 || 0,
            linkDepth: 3,
            orderBy: !searchTerm ? OrderBy.asc('name') : undefined,
            fields: [
                'sys',
                'name',
                'uri',
                'slug',
                'courseCardImage',
                'state',
                'degreeType',
                'compareEntryRequirements',
                'compareFeatures',
                'currentAcademicYear',
                'courseCategory',
                'courseListingFilterOptions'
            ]
        });

        const coursesVariantsQuery = [
            Op.or(
                Op.and(
                    Op.in(
                        'sys.contentTypeId',
                        'courseUndergraduateVariant',
                        'courseFoundationYearVariant',
                        'coursePostgraduateVariant',
                        'courseApprenticeshipsVariant'
                    ),
                    Op.in('container.sys.id', ...resCourses.items.map(course => course.sys.id)),
                    Op.in('year', ...[...new Set([queryYearKey])])
                )
            )
        ];

        const resCourseVariants = await search(coursesVariantsQuery, {
            pageSize: 120,
            fields: ['container', 'year', 'sys', 'name', 'slug', 'defaultVariant']
        });

        return {
            entry: resPage.entry,
            ancestors,
            courses: resCourses,
            courseVariants: resCourseVariants.items,
            filters,
            toggle,
            activeFilterQueries: {
                studyLevels: queryStudyLevels,
                courseOptions: queryCourseOptions,
                courseCategories: queryCategories
            },
            activeAcademicYear: queryYearName,
            searchTerm
        };
    });

    if (error) {
        return {
            notFound: true
        };
    } else {
        setCacheHeaders(res, surrogateKeys);

        return {
            props: data
        };
    }
};

export default CourseListing;
